import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { 
  Plus, Edit2, Eye, Trash2, Star, Search, Filter, 
  FileText, Clock, CheckCircle, Calendar, RefreshCw,
  Upload, Download
} from 'lucide-react';
import { toast } from 'sonner';
import InteractiveArticleEditor from './InteractiveArticleEditor';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InteractiveArticlesList = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [filter, setFilter] = useState({ status: '', category: '', featured: '', search: '' });
  const [importing, setImporting] = useState(false);

  // Load articles
  const loadArticles = useCallback(async (activeFilter) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter.status) params.append('status', activeFilter.status);
      if (activeFilter.category) params.append('category', activeFilter.category);
      if (activeFilter.featured === 'true') params.append('featured', 'true');
      if (activeFilter.search) params.append('search', activeFilter.search);

      const response = await axios.get(`${API}/interactive-articles?${params.toString()}`);
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Failed to load articles:', error);
      toast.error('Failed to load articles');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      loadArticles(filter);
    }, 300);
    return () => clearTimeout(timer);
  }, [filter, loadArticles]);

  // Import legacy articles
  const handleImportLegacy = async () => {
    setImporting(true);
    try {
      const response = await axios.post(`${API}/interactive-articles/import-legacy`);
      if (response.data.count > 0) {
        toast.success(`Imported ${response.data.count} articles`);
        loadArticles(filter);
      } else {
        toast.info('No new articles to import');
      }
    } catch (error) {
      toast.error('Failed to import articles');
    } finally {
      setImporting(false);
    }
  };

  // Delete article
  const handleDelete = async (articleId) => {
    if (!window.confirm('Are you sure you want to delete this article? This cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/interactive-articles/${articleId}`);
      toast.success('Article deleted');
      loadArticles(filter);
    } catch (error) {
      toast.error('Failed to delete article');
    }
  };

  // Open editor
  const openEditor = (article = null) => {
    setSelectedArticle(article?.article_id || null);
    setShowEditor(true);
  };

  // Close editor
  const closeEditor = () => {
    setShowEditor(false);
    setSelectedArticle(null);
    loadArticles(filter);
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'published':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800"><CheckCircle className="w-3 h-3" /> Published</span>;
      case 'draft':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"><FileText className="w-3 h-3" /> Draft</span>;
      case 'scheduled':
        return <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"><Calendar className="w-3 h-3" /> Scheduled</span>;
      default:
        return <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100">{status}</span>;
    }
  };

  if (showEditor) {
    return (
      <InteractiveArticleEditor 
        articleId={selectedArticle}
        onClose={closeEditor}
        onSave={closeEditor}
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-[#00204E]">Interactive Articles</h2>
          <p className="text-gray-600">Create and manage block-based articles with game cards, polls, and more</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleImportLegacy} disabled={importing}>
            <Upload className="w-4 h-4 mr-2" />
            {importing ? 'Importing...' : 'Import Legacy'}
          </Button>
          <Button onClick={() => openEditor()} className="bg-[#00204E]">
            <Plus className="w-4 h-4 mr-2" />
            New Article
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center bg-gray-50 p-4 rounded-lg">
        <div className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search articles..."
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border rounded-lg"
            />
          </div>
        </div>
        
        <select
          value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="">All Status</option>
          <option value="published">Published</option>
          <option value="draft">Draft</option>
          <option value="scheduled">Scheduled</option>
        </select>

        <select
          value={filter.category}
          onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="">All Categories</option>
          <option value="SWAC Football">SWAC Football</option>
          <option value="Basketball">Basketball</option>
          <option value="Jackson State">Jackson State</option>
          <option value="HBCU">HBCU</option>
        </select>

        <select
          value={filter.featured}
          onChange={(e) => setFilter({ ...filter, featured: e.target.value })}
          className="px-3 py-2 border rounded-lg"
        >
          <option value="">All Articles</option>
          <option value="true">Featured Only</option>
        </select>

        <Button variant="outline" onClick={() => loadArticles(filter)}>
          <RefreshCw className="w-4 h-4" />
        </Button>
      </div>

      {/* Articles Table */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00204E]"></div>
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-600">No articles found</h3>
          <p className="text-gray-500 mb-4">Create your first interactive article or import existing ones</p>
          <div className="flex justify-center gap-2">
            <Button variant="outline" onClick={handleImportLegacy}>
              <Upload className="w-4 h-4 mr-2" />
              Import Legacy Articles
            </Button>
            <Button onClick={() => openEditor()}>
              <Plus className="w-4 h-4 mr-2" />
              Create New Article
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-white border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Featured</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase">Updated</th>
                <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {articles.map((article) => (
                <tr key={article.article_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {article.featured_image ? (
                        <img 
                          src={article.featured_image} 
                          alt="" 
                          className="w-12 h-8 object-cover rounded"
                        />
                      ) : (
                        <div className="w-12 h-8 bg-gray-200 rounded flex items-center justify-center">
                          <FileText className="w-4 h-4 text-gray-400" />
                        </div>
                      )}
                      <div>
                        <div className="font-medium text-gray-900 line-clamp-1">{article.title}</div>
                        {article.subtitle && (
                          <div className="text-sm text-gray-500 line-clamp-1">{article.subtitle}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    {getStatusBadge(article.status)}
                  </td>
                  <td className="px-4 py-3">
                    {article.homepage?.is_featured ? (
                      <Star className="w-5 h-5 text-yellow-500 fill-yellow-500" />
                    ) : (
                      <Star className="w-5 h-5 text-gray-300" />
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-gray-600">{article.category}</span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 text-sm text-gray-500">
                      <Clock className="w-3 h-3" />
                      {formatDate(article.updated_at)}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <button
                        onClick={() => openEditor(article)}
                        className="p-2 hover:bg-gray-100 rounded"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4 text-gray-600" />
                      </button>
                      {article.status === 'published' && article.seo?.slug && (
                        <a
                          href={`/${article.seo.slug}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-2 hover:bg-gray-100 rounded"
                          title="View Live"
                        >
                          <Eye className="w-4 h-4 text-blue-600" />
                        </a>
                      )}
                      <button
                        onClick={() => handleDelete(article.article_id)}
                        className="p-2 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Quick Tips */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-bold text-blue-800 mb-2">💡 Quick Tips</h4>
        <ul className="text-sm text-blue-700 space-y-1">
          <li>• Use <strong>Game Card</strong> blocks to easily add SWAC matchups with team logos, times, and live stats links</li>
          <li>• Click <strong>Import Legacy</strong> to bring in existing HTML articles for editing</li>
          <li>• Enable <strong>Homepage Feature</strong> in Settings to make an article the main featured story</li>
          <li>• All changes are saved with version history - you can always restore previous versions</li>
        </ul>
      </div>
    </div>
  );
};

export default InteractiveArticlesList;
